/**
 * Utility functions for converting Place data to entity structures
 */

import { parseDateToYear } from './map-helpers';
import { extractErasFromContexts } from './TimelineSlider';
import { resolveDisplayDate } from '@/lib/date-utils';

/**
 * Convert a Place to a GeographicEntity structure for passage-specific maps
 *
 * @param place - Place to convert
 * @param passageRelevance - Passage-specific relevance description (defaults to place summary)
 * @returns GeographicEntity structure with historical context
 */
export function convertPlaceToEntity(place: Place, passageRelevance?: string): Omit<GeographicEntity, 'verseRef' | 'importance'> {
    return {
        placeName: place.placeName,
        type: place.type,
        coordinates: {
            latitude: place.coordinates.latitude,
            longitude: place.coordinates.longitude
        },
        coordinatePrecision: place.coordinates.precision,
        coordinateConfidence: place.coordinates.confidence,
        modernReference: place.coordinates.modernReference,
        summary: place.summary,
        description: place.description,
        passageRelevance: passageRelevance || place.summary,
        historicalContext: place.historicalPeriods?.map(period => ({
            period: period.period,
            eraLabel: period.period,
            displayDateRange: period.dates,
            sortableYearStart: parseDateToYear(period.dates, true),
            sortableYearEnd: parseDateToYear(period.dates, false),
            mapFact: period.description
        })) || [],
        relatedHistoricalEvents: place.relatedHistoricalEvents,
        sources: place.sources
    };
}

export interface AncientWorldMapTimelineData {
    minYear: number;
    maxYear: number;
    eras: TimelineEra[];
    initialYear: number;
    placeTypes: Set<string>;
}

/**
 * Initialize timeline data and filter sets from all places
 * 
 * This function:
 * - Extracts historical contexts from places
 * - Calculates the time range from contexts
 * - Extracts eras for the timeline
 * - Determines the initial year (midpoint)
 * - Collects all place types
 * 
 * @param allPlaces - All places to initialize from
 * @returns Initialized timeline and filter data
 */
export function initializeAncientWorldMapTimeline(allPlaces: Place[]): AncientWorldMapTimelineData {
    // Extract all historical contexts from places
    const allContexts: HistoricalContext[] = [];
    allPlaces.forEach(place => {
        if (place.historicalPeriods) {
            place.historicalPeriods.forEach(period => {
                allContexts.push({
                    period: period.period,
                    eraLabel: period.period,
                    displayDateRange: period.dates,
                    sortableYearStart: parseDateToYear(period.dates, true),
                    sortableYearEnd: parseDateToYear(period.dates, false),
                    mapFact: period.description
                });
            });
        }
    });

    // Extract eras
    const eras = extractErasFromContexts(allContexts);

    // Calculate time range
    let minYear = -2000;
    let maxYear = 100;

    allContexts.forEach(ctx => {
        minYear = Math.min(minYear, ctx.sortableYearStart);
        maxYear = Math.max(maxYear, ctx.sortableYearEnd);
    });

    // Calculate initial year (midpoint)
    const initialYear = Math.floor((minYear + maxYear) / 2);

    // Collect place types
    const placeTypes = new Set<string>(allPlaces.map(p => p.type));

    return {
        minYear,
        maxYear,
        eras,
        initialYear,
        placeTypes
    };
}

export interface FilteredAncientWorldMapData {
    visiblePlaces: Place[];
    placeTypeCounts: Map<string, number>;
    visibleEvents: HistoricalEvent[];
    eventCategoryCounts: Map<string, number>;
}

/**
 * Filter places and events by timeline and type, and calculate counts
 *
 * @param allPlaces - All places to filter
 * @param allEvents - All events to filter
 * @param selectedYear - Currently selected year on the timeline
 * @param selectedPlaceTypes - Set of selected place types
 * @param selectedEventCategories - Set of selected event categories
 * @returns Filtered places, events, and counts
 */
export function filterAncientWorldMapPlaces(
    allPlaces: Place[],
    allEvents: HistoricalEvent[],
    selectedYear: number,
    selectedPlaceTypes: Set<string>,
    selectedEventCategories: Set<string>,
    preferredChronology?: BiblicalChronology
): FilteredAncientWorldMapData {

    if (allPlaces.length === 0) {
        return {
            visiblePlaces: [],
            placeTypeCounts: new Map(),
            visibleEvents: [],
            eventCategoryCounts: new Map()
        };
    }

    // Filter places that have coordinates and historical periods
    const validPlaces = allPlaces.filter(place =>
        place.coordinates && place.historicalPeriods && place.historicalPeriods.length > 0
    );

    // Filter places by timeline
    const timeFilteredPlaces = validPlaces.filter(place =>
        place.historicalPeriods.some(period => {
            const startYear = parseDateToYear(period.dates, true);
            const endYear = parseDateToYear(period.dates, false);
            return startYear <= selectedYear && endYear >= selectedYear;
        })
    );

    // Filter places by type
    const visiblePlaces = timeFilteredPlaces.filter(place => selectedPlaceTypes.has(place.type));

    // Calculate place counts
    const placeTypeCounts = new Map<string, number>();
    timeFilteredPlaces.forEach(place => {
        placeTypeCounts.set(place.type, (placeTypeCounts.get(place.type) || 0) + 1);
    });

    // Normalize and Filter events by timeline
    const timeFilteredEvents = allEvents.map(event => {
        const { activeDate, alternatives } = resolveDisplayDate(event.date, event.alternativeDates, preferredChronology);
        return {
            ...event,
            date: activeDate,
            alternativeDates: alternatives
        };
    }).filter(event =>
        event.date.startYear <= selectedYear &&
        (event.date.endYear || event.date.startYear) >= selectedYear
    );


    // Filter events by category
    const visibleEvents = timeFilteredEvents.filter(event =>
        event.categories.some(cat => selectedEventCategories.has(cat.category))
    );

    // Calculate event category counts
    const eventCategoryCounts = new Map<string, number>();
    timeFilteredEvents.forEach(event => {
        event.categories.forEach(cat => {
            eventCategoryCounts.set(cat.category, (eventCategoryCounts.get(cat.category) || 0) + 1);
        });
    });

    return { visiblePlaces, placeTypeCounts, visibleEvents, eventCategoryCounts };
}
